import { useContext, useEffect, useMemo, useRef, useState } from "react";
import services from "../../../../services/services";
import { Urls } from "../../../../services/Urls";
import { faCopy, faPenToSquare, faTrashCan } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import _ from "underscore";
import { ToastContext } from "../../../../context/ToastContext";
import { callautopopulate } from "../../../../services/callautopop";

export const useTaskStatus = () => {

    const { showSuccess, showInfo, showError } = useContext(ToastContext);
    const [formValues, setFormValues] = useState({ visible: false });
    const [options, setOptions] = useState({ category: [] });
    const [loader, setLoader] = useState({ grid: false });
    const [rowData, setRowData] = useState([]);
    const scrollRef = useRef(null);
    const scrollPosition = useRef(0);
    const columns = [
        {
            key: "order_number",
            label: "Sr No.",
            style: { width: "5%", textAlign: "center" },
            headerStyle: { textAlign: "center" },
        },
        {
            key: "status_name",
            label: "Status Name",
            style: { width: "40%" },
            render: (row) => <div>
                <div className="fw-bold text-truncate" style={{ fontSize: "12px" }} title={row?.status_name}>{row?.status_name}</div>
                <div className="fw-semibold text-truncate" style={{ fontSize: "10px", color: "#a1a1a1" }} title={row?.status_description}>{row?.status_description}</div>
            </div>
        },
        {
            key: "status_category",
            label: "Category Name",
            style: { width: "15%" },
            render: (row) => <div className="fw-semibold">{row?.status_category}</div>
        },
        {
            key: "workflows",
            label: "Workflows",
            style: { width: "25%" }
        },
        {
            key: "order",
            label: "Order",
            style: { width: "5%", textAlign: "center" },
            headerStyle: { textAlign: "center" },
            render: (row) => !formValues?.search ? <div className="d-flex gap-3 text-secondary justify-content-center">
                <div className={row?.sr_no <= 1 ? "invisible" : "c-pointer"} onClick={() => handleReorder(row, true)}><FontAwesomeIcon icon="fa-solid fa-arrow-up" /></div>
                <div className={row?.sr_no >= rowData?.length ? "invisible" : "c-pointer"} onClick={() => handleReorder(row, false)}><FontAwesomeIcon icon="fa-solid fa-arrow-down" /></div>
            </div> : <div>{row?.order_number}</div>
        },
        {
            key: "action",
            label: "Actions",
            sort: false,
            style: { width: "10%", textAlign: "center" },
            headerStyle: { textAlign: "center" },
            render: (row) => <div className="d-flex gap-3 align-items-center justify-content-center">
                <div className="c-pointer text-primary" title="Edit" onClick={() => setFormValues((prev) => ({ ...prev, visible: true, editData: row, name: row?.status_name, description: row?.status_description, category: row?.status_category, order: row?.order_number }))}><FontAwesomeIcon icon={faPenToSquare} /></div>
                <div className="c-pointer text-danger" title="Delete" onClick={() => setFormValues((prev) => ({ ...prev, editData: row, isDelete: true }))}><FontAwesomeIcon icon={faTrashCan} /></div>
            </div>
        },
    ];
    const formFields = [
        {
            label: "Status Name",
            name: "name",
            col: "12",
            type: "text",
            required: true,
        },
        {
            label: "Category",
            name: "category",
            col: "6",
            type: "select",
            option: options?.category,
            required: true,
        },
        {
            label: "Order",
            name: "order",
            col: "6",
            type: "text",
            required: true,
        },
        {
            label: "Description",
            name: "description",
            col: "12",
            type: "textarea",
        },
    ]

    useEffect(() => {
        callautopopulate("status_category")?.then(res => {
            setOptions((prev) => ({ ...prev, category: _.pluck(res?.status_category, "status_category")?.sort((a, b) => b?.localeCompare(a)) }));
        })
        getIssueStatus();
    }, [])

    const getIssueStatus = async () => {
        try {
            setLoader((prev) => ({ ...prev, grid: true }));
            const res = await services.getInvokeRemote("GET", Urls("GETISSUESTATUS"), {});
            if (res?.body?.error_status === false) {
                const data = res?.body?.data?.priority_list?.sort((a, b) => a?.order_number - b?.order_number);
                setRowData(data);
                setTimeout(() => {
                    if (scrollRef.current) {
                        scrollRef.current.scrollTop = scrollPosition?.current;
                    }
                }, 0);
            }
        } catch (error) {
            console.error("Error: ", error);
        } finally {
            setLoader((prev) => ({ ...prev, grid: false }));
        }
    }

    const saveStatus = async (row, order, swap) => {
        try {
            if (scrollRef.current) {
                scrollPosition.current = scrollRef?.current?.scrollTop;
            }
            if (!formValues?.name && !formValues?.isDelete && !order) {
                showInfo("Info", "Please enter the status name");
                return;
            } else if (!formValues?.category && !formValues?.isDelete && !order) {
                showInfo("Info", "Please select category");
                return;
            }
            setLoader((prev) => ({ ...prev, btn: true }));
            const payload = {
                status_id: (order ? row?.status_id : formValues?.editData?.status_id) || 0,
                status_name: (order ? row?.status_name : formValues?.name) || "",
                status_description: (order ? row?.status_description : formValues?.description) || "",
                status_category: (order ? row?.status_category : formValues?.category) || "",
                order_number: (order ? order : formValues?.order) || 0,
                is_deleted: formValues?.isDelete || false,
                is_swapp: swap || false
            }
            const res = await services.getInvokeRemote("POST", Urls("UPSERTISSUESTATUS"), payload);
            if (res?.body?.error_status === false) {
                showSuccess("success", res?.body?.message);
                setFormValues({ visible: false });
                if (formValues?.isDelete) {
                    setRowData((prev) => prev?.filter(item => item?.status_id !== payload?.status_id));
                } else {
                    getIssueStatus();
                }
            } else {
                res?.body?.message && showError("error", res?.body?.message);
            }
        } catch (error) {
            console.error("Error: ", error);
            error?.response?.data?.errorMessage && showError("error", error?.response?.data?.errorMessage);
        } finally {
            setLoader((prev) => ({ ...prev, btn: false }));
        }
    }

    const handleReorder = (row, up) => {
        let order = 0;
        let data = [...rowData];
        const index = _.indexOf(data, _.findWhere(data, { status_id: row?.status_id }));
        if (index !== -1 && data[index]) {
            if (up) {
                order = data[index].order_number - 1;
            } else {
                order = data[index].order_number + 1;
            }
            // setRowData(data);
            saveStatus(row, order, true);
        }
    }

    const filteredData = useMemo(() => {
        if (!formValues?.search?.trim()) return rowData;
        return rowData?.filter((item) => item?.status_name?.toLowerCase()?.includes(formValues?.search.toLowerCase()));
    }, [rowData, formValues?.search]);

    return { columns, rowData: filteredData, loader, getIssueStatus, formFields, formValues, setFormValues, saveStatus, scrollRef }
}